import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useDebouncedCallback } from 'use-debounce';

import useAnalytics from 'config/analytics';
import { useSelector } from 'hooks/useSelector';

import { closeImportTScans, importAuditsOrTscans } from 'modules/interventions/actions/details';
import { getImportedTireScanIds, isLoadingTScansImport, getSelectedIntervention } from 'modules/interventions/selectors';
import { getInterventionDealerId } from 'modules/interventions/selectors/details';
import { fetchTireScans } from 'modules/tireScan/actions';
import { IMPORT_SOURCE } from 'modules/tireScan/constants';
import { getUIImportEntities, isFetching, hasError, hasNext, getNextPageParams } from 'modules/tireScan/selectors';

import { ImportModal, Input } from 'ui/base';

import ListItem from './ListItem';

export const MAX_IMPORTABLE_TSCANS = 5;
export const REGISTRATION_MIN_SIZE = 3;

/** @type {React.FC} */
const ImportTScanModal = () => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();

  const isLoading = useSelector(isLoadingTScansImport);
  const dealerId = useSelector(getInterventionDealerId);
  const { id: interventionId, mode, vehicleInfo = {} } = useSelector(getSelectedIntervention);

  const hasMore = useSelector(hasNext);
  const apiError = useSelector(hasError);
  const loading = useSelector(isFetching);
  const entries = useSelector(getUIImportEntities);
  const nextParams = useSelector(getNextPageParams);

  const importedIds = useSelector(getImportedTireScanIds);

  const [tireScanIds, setTireScanIds] = React.useState([]);
  const [registration, setRegistration] = React.useState(vehicleInfo.registration);

  const exceeded = tireScanIds.length > MAX_IMPORTABLE_TSCANS;
  const disabled = tireScanIds.length === 0 || exceeded;

  const dataSource = React.useMemo(
    () => entries.filter(({ id: tireScanId }) => !importedIds.includes(tireScanId)),
    [entries, importedIds]
  );

  const getTScans = React.useCallback(
    (newRegistration, refresh = true) => {
      const canFetch = newRegistration?.length >= REGISTRATION_MIN_SIZE;
      if (canFetch) {
        dispatch(
          fetchTireScans(
            {
              dealerId,
              registration: newRegistration,
              ...(refresh ? {} : nextParams),
            },
            { source: IMPORT_SOURCE.MODAL }
          )
        );
      }
    },
    [dispatch, dealerId, nextParams]
  );

  const handleSearchChange = useDebouncedCallback(getTScans, 500);

  const handlePlateOnChange = React.useCallback(
    ({ target }) => {
      const { value: newValue } = target;
      setRegistration(newValue);
      handleSearchChange(newValue);
    },
    [handleSearchChange]
  );

  const handleLoadMore = React.useCallback(() => {
    getTScans(registration, false);
  }, [getTScans, registration]);

  const handleClose = React.useCallback(() => {
    dispatch(closeImportTScans());
  }, [dispatch]);

  /**  Import the selected audits into the current intervention */
  const handleImport = React.useCallback(
    (event) => {
      event.stopPropagation();
      trackEvent({ category: 'Audit Files', action: 'Import', label: 'Tire Scan' });
      dispatch(importAuditsOrTscans(interventionId, mode, 'tireScans', tireScanIds));
    },
    [dispatch, interventionId, mode, tireScanIds, trackEvent]
  );

  const renderItem = React.useCallback(
    (tscan) => <ListItem key={tscan.id} {...tscan} dealerId={dealerId} onChange={setTireScanIds} value={tireScanIds} />,
    [dealerId, tireScanIds]
  );

  return (
    <ImportModal
      title={<FormattedMessage id="page.fiche.import_tscan_modal.title" defaultMessage="Import a tire scan report" />}
      onConfirm={handleImport}
      okButtonProps={{ disabled, loading: isLoading }}
      onClose={handleClose}
      alertMessage={
        exceeded && (
          <FormattedMessage
            id="mediaLibrary.errorMaxMedia"
            defaultMessage="You have exceeded the maximum number of files to import"
          />
        )
      }
      description={
        <FormattedMessage id="page.fiche.form.vehicle_information.plate_number" defaultMessage="Plate number" />
      }
      search={<Input.Search value={registration} onChange={handlePlateOnChange} id="import-form-search" />}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      apiError={apiError}
      loading={loading}
      dataSource={dataSource}
      renderItem={renderItem}
    />
  );
};

export default ImportTScanModal;
